// Upscale (M13, Pro mode): size (2× / 4× / original), model, strength. Changing the model or strength
// re-runs the upscale on release (one undo step); the result is cached per photo + model + size + strength.
import { useEffect, useState } from 'react';
import { Slider } from '../controls/Slider';
import { useActive } from './useActive';
import { useT } from '../../i18n';
import { MakeBigger } from '../simple/MakeBigger';
import { checkUpscaleModels, makeBigger, useUpscale } from '../../features/upscale/upscale';
import { useLibrary } from '../../state/library';

export function UpscalePanel() {
  const t = useT();
  const { settings, update } = useActive();
  const id = useLibrary((s) => s.activeId);
  const available = useUpscale((u) => u.available);
  const last = useUpscale((u) => u.lastRun);
  useEffect(() => void checkUpscaleModels(), []);
  const u = settings.upscale;
  const [live, setLive] = useState<number | null>(null);

  const rerun = (patch: Partial<typeof u>) => {
    if (!id) return;
    if (u.factor > 1) void makeBigger(id, u.factor, patch);
    else update((s) => void Object.assign(s.upscale, patch));
  };

  return (
    <div data-testid="upscale-panel">
      <div style={{ fontSize: 12, marginBottom: 4 }}>{t('upscaleFactor')}</div>
      <MakeBigger big={false} />
      <label style={{ display: 'block', fontSize: 12, margin: '8px 0 2px' }}>{t('upscaleModel')}</label>
      <select
        data-testid="upscale-model"
        value={u.model}
        onChange={(e) => rerun({ model: e.target.value as typeof u.model })}
        style={{ width: '100%', marginBottom: 6 }}
      >
        <option value="general">{t('upscaleModelGeneral')}</option>
        <option value="plus" disabled={!available?.includes('plus')}>
          {t('upscaleModelPlus')}
          {available && !available.includes('plus') ? ` — ${t('upscaleModelMissing')}` : ''}
        </option>
      </select>
      <Slider
        label="upscaleStrength"
        testId="slider-upscale-strength"
        value={live ?? u.strength}
        min={0}
        max={100}
        step={1}
        defaultValue={50}
        onChange={(v, final) => {
          setLive(final ? null : v);
          if (final) rerun({ strength: v });
        }}
      />
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t('upscaleStrengthHint')}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{t('upscaleNote')}</div>
      {last && (
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }} data-testid="upscale-last">
          {last.width} × {last.height} · {last.tiles} tiles · {last.ep} · {(last.totalMs / 1000).toFixed(1)} s
        </div>
      )}
    </div>
  );
}
